import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

import {
    FaWhatsapp,
    FaFacebookF,
    FaXTwitter,
    FaLinkedinIn,
    FaLink,
    FaArrowRight,
    FaClock,
    FaChevronRight,
    FaHotel,
    FaCompass,
    FaShareNodes,
    FaCalendarDays,
    FaHouse,
} from "react-icons/fa6";

import blogImg from "../assests/images/blog-bg.jpg";

const API_URL = "https://cheapdubaihotels.com/api";
const SITE_URL = "https://cheapdubaihotels.com";

const COLORS = {
    navy: "#062F4F",
    teal: "#0B6E8E",
    gold: "#F7B928",
    orange: "#FF5A1F",
    background: "#F7FAFC",
    border: "#DFE8ED",
    muted: "#64748B",
};

export default function BlogDetail() {
    const { slug } = useParams();

    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let active = true;

        const fetchData = async () => {
            setLoading(true);
            setBlog(null);

            try {
                const [blogResponse, latestResponse] = await Promise.allSettled([
                    axios.get(`${API_URL}/blogs/${slug}`),
                    axios.get(`${API_URL}/blogs`),
                ]);

                if (!active) return;

                if (blogResponse.status === "fulfilled") {
                    const data = blogResponse.value.data;
                    setBlog(data.blog || data);
                } else {
                    console.error("Failed to load blog:", blogResponse.reason);
                    setBlog(null);
                }

                if (latestResponse.status === "fulfilled") {
                    const responseData = latestResponse.value.data;
                    const data = responseData.blogs || responseData;
                    setLatestBlogs(Array.isArray(data) ? data : []);
                } else {
                    setLatestBlogs([]);
                }
            } catch (error) {
                if (active) {
                    console.error("Failed to load blog data:", error);
                    setBlog(null);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [slug]);

    const currentUrl =
        typeof window !== "undefined"
            ? window.location.href
            : `${SITE_URL}/blogs/${slug}`;

    const canonicalUrl = `${SITE_URL}/blogs/${blog?.slug || slug}`;

    const getImage = (image, fallback = blogImg) => {
        if (!image) return fallback;

        if (
            image.startsWith("http://") ||
            image.startsWith("https://") ||
            image.startsWith("data:")
        ) {
            return image;
        }

        return `${API_URL.replace("/api", "")}/uploads/blogs/${image.replace(
            /^\/+/,
            ""
        )}`;
    };

    const bannerImage = blog
        ? getImage(blog.banner_image || blog.image)
        : blogImg;

    const formatDate = (date) => {
        if (!date) return "";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) return "";

        return parsedDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatShortDate = (date) => {
        if (!date) return "";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) return "";

        return parsedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const readingTime = useMemo(() => {
        if (!blog?.content) return "1 min read";

        const text = blog.content
            .replace(/<[^>]*>/g, " ")
            .replace(/&nbsp;/g, " ")
            .trim();

        const words = text ? text.split(/\s+/).length : 0;

        return `${Math.max(1, Math.ceil(words / 200))} min read`;
    }, [blog]);

    const relatedBlogs = latestBlogs
        .filter((item) => item.slug !== blog?.slug)
        .filter((item) => {
            if (!blog?.category?.id) return true;

            return String(item.category?.id) === String(blog.category.id);
        })
        .slice(0, 3);

    const copyLink = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(currentUrl);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = currentUrl;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";

                document.body.appendChild(textArea);
                textArea.select();

                const success = document.execCommand("copy");
                document.body.removeChild(textArea);

                if (!success) throw new Error("Unable to copy link");
            }

            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error("Copy failed:", error);
        }
    };

    const shareButtons = [
        {
            name: "WhatsApp",
            icon: <FaWhatsapp />,
            href: `https://wa.me/?text=${encodeURIComponent(
                `${blog?.title || ""} ${currentUrl}`
            )}`,
            className: "bg-[#25D366] hover:bg-[#1ebe5d]",
        },
        {
            name: "Facebook",
            icon: <FaFacebookF />,
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                currentUrl
            )}`,
            className: "bg-[#1877F2] hover:bg-[#0d65d9]",
        },
        {
            name: "X",
            icon: <FaXTwitter />,
            href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                currentUrl
            )}&text=${encodeURIComponent(blog?.title || "")}`,
            className: "bg-[#111827] hover:bg-black",
        },
        {
            name: "LinkedIn",
            icon: <FaLinkedinIn />,
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                currentUrl
            )}`,
            className: "bg-[#0A66C2] hover:bg-[#084f96]",
        },
    ];

    const imageFallback = (event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = blogImg;
    };

    // LOADING
    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center bg-[#F7FAFC] px-4">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#DFE8ED] border-t-[#0B6E8E]" />
                    <p className="mt-4 text-sm font-medium text-slate-500">
                        Loading travel story...
                    </p>
                </div>
            </div>
        );
    }

    // NOT FOUND
    if (!blog) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center bg-[#F7FAFC] px-5 py-16">
                <div className="max-w-md text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EAF5F8] text-[#0B6E8E]">
                        <FaCompass className="text-2xl" />
                    </div>

                    <h1 className="mt-5 text-3xl font-bold text-[#062F4F]">
                        Article Not Found
                    </h1>

                    <p className="mt-3 text-sm leading-7 text-slate-500">
                        We couldn't find this article. Browse our latest travel stories
                        for more Dubai inspiration.
                    </p>

                    <Link
                        to="/blogs"
                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#FF5A1F] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#e94b14]"
                    >
                        Browse Travel Stories
                        <FaArrowRight />
                    </Link>
                </div>
            </div>
        );
    }

    const description =
        blog.meta_description || blog.excerpt || blog.title;

    return (
        <>
            <Helmet>
                <title>
                    {blog.meta_title || `${blog.title} | Cheap Dubai Hotels`}
                </title>

                <meta name="description" content={description} />

                <link rel="canonical" href={canonicalUrl} />

                <meta property="og:title" content={blog.meta_title || blog.title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={bannerImage} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blog.meta_title || blog.title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={bannerImage} />

                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="min-h-screen bg-[#F7FAFC] text-[#062F4F]">
                {/* COMPACT ARTICLE BANNER */}
                <section className="relative overflow-hidden bg-[#062F4F]">
                    <img
                        src={bannerImage}
                        alt={blog.title}
                        onError={imageFallback}
                        className="absolute inset-0 h-full w-full object-cover opacity-30"
                    />

                    <div className="absolute inset-0 bg-gradient-to-r from-[#062F4F]/95 via-[#062F4F]/85 to-[#062F4F]/65" />

                    <div className="relative mx-auto max-w-[1536px] px-5 py-9 sm:px-8 sm:py-11 lg:px-10 lg:py-14">
                        {/* BREADCRUMB */}
                        <nav
                            aria-label="Breadcrumb"
                            className="mb-6 flex flex-wrap items-center gap-2 text-xs text-white/70"
                        >
                            <Link
                                to="/"
                                className="inline-flex items-center gap-1.5 transition hover:text-[#F7B928]"
                            >
                                <FaHouse />
                                Home
                            </Link>

                            <FaChevronRight className="text-[9px] text-white/40" />

                            <Link
                                to="/blogs"
                                className="transition hover:text-[#F7B928]"
                            >
                                Travel Blog
                            </Link>

                            <FaChevronRight className="text-[9px] text-white/40" />

                            <span className="max-w-[200px] truncate text-white">
                                {blog.title}
                            </span>
                        </nav>

                        <div className="max-w-4xl">
                            <span className="inline-flex items-center gap-2 rounded-lg border border-[#F7B928]/40 bg-[#F7B928]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#F7B928]">
                                <FaCompass />
                                {blog.category?.name || "Dubai Travel Guide"}
                            </span>

                            <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
                                {blog.title}
                            </h1>

                            {blog.excerpt && (
                                <p className="mt-4 max-w-3xl text-sm leading-7 text-white/80 sm:text-base">
                                    {blog.excerpt}
                                </p>
                            )}

                            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-white/75 sm:text-sm">
                                {blog.created_at && (
                                    <span className="flex items-center gap-2">
                                        <FaCalendarDays className="text-[#F7B928]" />
                                        {formatDate(blog.created_at)}
                                    </span>
                                )}

                                <span className="flex items-center gap-2">
                                    <FaClock className="text-[#F7B928]" />
                                    {readingTime}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#0B6E8E] via-[#F7B928] to-[#FF5A1F]" />
                </section>

                {/* ARTICLE LAYOUT */}
                <section className="mx-auto max-w-[1536px] px-5 py-8 sm:px-8 sm:py-12 lg:px-10 lg:py-14">
                    <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
                        {/* MAIN ARTICLE */}
                        <article className="min-w-0">
                            {/* ARTICLE SUMMARY */}
                            <div className="mb-6 rounded-2xl border border-[#DFE8ED] bg-white p-5 shadow-sm sm:p-7">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="rounded-lg bg-[#EAF5F8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0B6E8E]">
                                        {blog.category?.name || "Travel"}
                                    </span>

                                    {blog.created_at && (
                                        <span className="text-xs text-slate-500">
                                            Published {formatShortDate(blog.created_at)}
                                        </span>
                                    )}
                                </div>

                                <p className="mt-5 border-l-4 border-[#F7B928] pl-4 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
                                    {blog.excerpt ||
                                        "Explore helpful travel advice and inspiration for your next Dubai getaway."}
                                </p>
                            </div>

                            {/* ARTICLE BODY */}
                            <div className="min-w-0 rounded-2xl border border-[#DFE8ED] bg-white px-5 py-7 shadow-sm sm:px-8 sm:py-10">
                                <div
                                    className="
                    blog-content
                    prose prose-slate max-w-none
                    prose-headings:font-bold
                    prose-headings:tracking-tight
                    prose-headings:text-[#062F4F]
                    prose-h2:mt-9 prose-h2:text-2xl
                    prose-h3:mt-7 prose-h3:text-xl
                    prose-p:text-[15px] prose-p:leading-8
                    prose-p:text-slate-600
                    sm:prose-p:text-base
                    prose-a:font-semibold
                    prose-a:text-[#0B6E8E]
                    prose-a:no-underline
                    hover:prose-a:underline
                    prose-strong:text-[#062F4F]
                    prose-li:text-slate-600
                    prose-img:rounded-xl
                    prose-img:shadow-md
                    prose-blockquote:border-l-[#F7B928]
                    prose-blockquote:bg-[#F7FAFC]
                    prose-blockquote:px-5
                    prose-blockquote:py-2
                    prose-blockquote:text-slate-600
                  "
                                    dangerouslySetInnerHTML={{ __html: blog.content || "" }}
                                />

                                {/* ARTICLE END */}
                                <div className="mt-9 border-t border-[#EDF2F5] pt-6">
                                    <Link
                                        to="/blogs"
                                        className="inline-flex items-center gap-3 text-sm font-bold text-[#0B6E8E] transition hover:text-[#FF5A1F]"
                                    >
                                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF5F8]">
                                            <FaArrowRight className="rotate-180 text-xs" />
                                        </span>
                                        Back to Travel Blog
                                    </Link>
                                </div>
                            </div>

                            {/* SHARE ARTICLE */}
                            <div className="mt-6 rounded-2xl border border-[#DFE8ED] bg-white p-5 sm:p-6">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF5F8] text-[#0B6E8E]">
                                            <FaShareNodes />
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-bold text-[#062F4F]">
                                                Enjoyed this article?
                                            </h3>
                                            <p className="mt-1 text-xs text-slate-500">
                                                Share it with your friends.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        {shareButtons.map((item) => (
                                            <a
                                                key={item.name}
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Share on ${item.name}`}
                                                title={`Share on ${item.name}`}
                                                className={`flex h-10 w-10 items-center justify-center rounded-xl text-sm text-white transition hover:-translate-y-0.5 ${item.className}`}
                                            >
                                                {item.icon}
                                            </a>
                                        ))}

                                        <button
                                            type="button"
                                            onClick={copyLink}
                                            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#062F4F] px-3 text-xs font-bold text-white transition hover:bg-[#0B6E8E]"
                                        >
                                            <FaLink />
                                            {copied ? "Copied!" : "Copy link"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </article>

                        {/* SIDEBAR */}
                        <aside className="space-y-6">
                            {/* ABOUT THE JOURNAL */}
                            <div className="overflow-hidden rounded-2xl border border-[#DFE8ED] bg-white shadow-sm">
                                <div className="bg-[#062F4F] p-5">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F7B928] text-[#062F4F]">
                                            <FaCompass />
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#F7B928]">
                                                Travel Inspiration
                                            </p>
                                            <h2 className="mt-1 text-lg font-bold !text-white">
                                                Explore Dubai
                                            </h2>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5">
                                    <p className="text-sm leading-7 text-slate-500">
                                        Discover destination guides, hotel tips and useful ideas
                                        to help you plan your Dubai trip.
                                    </p>

                                    <Link
                                        to="/blogs"
                                        className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#0B6E8E] transition hover:text-[#FF5A1F]"
                                    >
                                        Browse all stories
                                        <FaArrowRight className="text-xs" />
                                    </Link>
                                </div>
                            </div>

                            {/* LATEST ARTICLES */}
                            <div className="rounded-2xl border border-[#DFE8ED] bg-white p-5 shadow-sm">
                                <div className="mb-5 flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#0B6E8E]">
                                            Read next
                                        </p>
                                        <h2 className="mt-1 text-lg font-bold text-[#062F4F]">
                                            Latest Stories
                                        </h2>
                                    </div>

                                    <Link
                                        to="/blogs"
                                        aria-label="View all blog articles"
                                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF5F8] text-[#0B6E8E] transition hover:bg-[#FF5A1F] hover:text-white"
                                    >
                                        <FaArrowRight className="text-xs" />
                                    </Link>
                                </div>

                                <div className="space-y-4">
                                    {latestBlogs
                                        .filter((item) => item.slug !== blog.slug)
                                        .slice(0, 5)
                                        .map((item) => (
                                            <Link
                                                key={item.id}
                                                to={`/blogs/${item.slug}`}
                                                className="group flex gap-3"
                                            >
                                                <div className="h-[76px] w-[76px] shrink-0 overflow-hidden rounded-xl bg-[#EAF5F8]">
                                                    <img
                                                        src={getImage(
                                                            item.thumbnail_image || item.image
                                                        )}
                                                        alt={item.title}
                                                        loading="lazy"
                                                        onError={imageFallback}
                                                        className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                                                    />
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <p className="line-clamp-2 text-sm font-bold leading-5 text-[#062F4F] transition group-hover:text-[#0B6E8E]">
                                                        {item.title}
                                                    </p>

                                                    {item.created_at && (
                                                        <p className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-400">
                                                            <FaCalendarDays className="text-[#0B6E8E]" />
                                                            {formatShortDate(item.created_at)}
                                                        </p>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}

                                    {latestBlogs.filter((item) => item.slug !== blog.slug)
                                        .length === 0 && (
                                            <p className="text-sm leading-6 text-slate-500">
                                                More travel stories will appear here soon.
                                            </p>
                                        )}
                                </div>
                            </div>

                            {/* HOTEL CTA */}
                            <div className="relative overflow-hidden rounded-2xl bg-[#062F4F] p-6">
                                <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-[#0B6E8E]/40 blur-2xl" />
                                <div className="absolute -bottom-12 -left-10 h-36 w-36 rounded-full bg-[#F7B928]/20 blur-2xl" />

                                <div className="relative">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F7B928] text-[#062F4F]">
                                        <FaHotel />
                                    </div>

                                    <h2 className="mt-5 text-xl font-bold !text-white">
                                        Planning a Dubai Stay?
                                    </h2>

                                    <p className="mt-3 text-sm leading-6 text-white/75">
                                        Explore hotels and find an option that suits your trip and
                                        budget.
                                    </p>

                                    <Link
                                        to="/hotels"
                                        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF5A1F] px-5 py-3 text-sm font-bold text-white transition hover:bg-white hover:text-[#062F4F]"
                                    >
                                        Explore Hotels
                                        <FaArrowRight className="text-xs" />
                                    </Link>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>

                {/* RELATED ARTICLES */}
                {relatedBlogs.length > 0 && (
                    <section className="border-t border-[#DFE8ED] bg-white">
                        <div className="mx-auto max-w-[1536px] px-5 py-12 sm:px-8 sm:py-16 lg:px-10">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0B6E8E]">
                                        Keep Exploring
                                    </p>

                                    <h2 className="mt-2 text-2xl font-bold text-[#062F4F] sm:text-3xl">
                                        More Travel Inspiration
                                    </h2>

                                    <p className="mt-2 text-sm text-slate-500">
                                        Continue discovering Dubai with these stories.
                                    </p>
                                </div>

                                <Link
                                    to="/blogs"
                                    className="inline-flex items-center gap-2 text-sm font-bold text-[#FF5A1F] transition hover:gap-3"
                                >
                                    View all articles
                                    <FaArrowRight className="text-xs" />
                                </Link>
                            </div>

                            <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                {relatedBlogs.map((item) => (
                                    <Link
                                        key={item.id}
                                        to={`/blogs/${item.slug}`}
                                        className="group overflow-hidden rounded-2xl border border-[#DFE8ED] bg-white transition hover:-translate-y-1 hover:shadow-xl"
                                    >
                                        <div className="h-48 overflow-hidden bg-[#EAF5F8]">
                                            <img
                                                src={getImage(item.thumbnail_image || item.image)}
                                                alt={item.title}
                                                loading="lazy"
                                                onError={imageFallback}
                                                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                                            />
                                        </div>

                                        <div className="p-5">
                                            {item.category?.name && (
                                                <span className="inline-block rounded-md bg-[#EAF5F8] px-3 py-1.5 text-[10px] font-bold text-[#0B6E8E]">
                                                    {item.category.name}
                                                </span>
                                            )}

                                            <h3 className="mt-3 line-clamp-2 text-lg font-bold leading-6 text-[#062F4F] transition group-hover:text-[#0B6E8E]">
                                                {item.title}
                                            </h3>

                                            {item.created_at && (
                                                <p className="mt-3 text-xs text-slate-400">
                                                    {formatShortDate(item.created_at)}
                                                </p>
                                            )}

                                            <div className="mt-4 flex items-center justify-between border-t border-[#EDF2F5] pt-4">
                                                <span className="text-xs font-bold text-[#FF5A1F]">
                                                    Read story
                                                </span>
                                                <FaArrowRight className="text-xs text-[#0B6E8E] transition group-hover:translate-x-1" />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* FINAL CTA */}
                <section className="bg-[#F7FAFC] px-5 py-10 sm:px-8 sm:py-14 lg:px-10">
                    <div className="relative mx-auto max-w-[1536px] overflow-hidden rounded-2xl bg-[#062F4F] px-6 py-9 sm:px-10 sm:py-12">
                        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#0B6E8E]/30 blur-3xl" />

                        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                            <div className="max-w-2xl">
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#F7B928]">
                                    Your next Dubai adventure
                                </span>

                                <h2 className="mt-2 text-2xl font-bold leading-tight !text-white sm:text-3xl">
                                    Turn your travel inspiration into a stay.
                                </h2>

                                <p className="mt-3 text-sm leading-6 text-white/75">
                                    Explore Dubai hotels and find a place that fits your plans.
                                </p>
                            </div>

                            <Link
                                to="/hotels"
                                className="inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-[#FF5A1F] px-6 py-3.5 text-sm font-bold text-white transition hover:bg-white hover:text-[#062F4F]"
                            >
                                Explore Hotels
                                <FaArrowRight />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}